import React from 'react';
import { useTheme } from 'styled-components';
import { TComponentSurfaceVariant } from 'types/ui.types';
import { HearthBeatLoaderSC } from './hb-loader.styled';
import { TLoaderSize } from './hb-loader.types';
import { getGradientCords, POINTS_MAP, STROKE_MAP, VIEWBOX_MAP } from './hb-loader.utils';

interface HearthBeatLoaderProps extends React.HtmlHTMLAttributes<HTMLDivElement> {
  loading?: boolean;
  variant?: TComponentSurfaceVariant;
  size?: TLoaderSize;
  trackColor?: string;
}

/**
 * Loader like hearth beat.
 * Inspired by https://codepen.io/avstorm/pen/qjQaJa?__cf_chl_captcha_tk__=pmd_h0m_BZynRvherh1Ipl2pZFMbcdUTDag2VIt_2T_wSXs-1629729978-0-gqNtZGzNAvujcnBszQd9
 */
const HearthBeatLoader: React.FunctionComponent<HearthBeatLoaderProps> = ({
  loading,
  variant = 'on-light',
  size = 'large',
  trackColor,
  ...rest
}) => {
  const theme = useTheme();

  if (!loading) {
    return null;
  }

  const stroke =
    size === 'large'
      ? 'url(#gradient)'
      : variant === 'on-dark'
      ? theme.color.tint.primaryDark
      : theme.color.tint.onPrimary;

  const points = POINTS_MAP[size];
  const strokeWidth = STROKE_MAP[size];
  const viewBox = VIEWBOX_MAP[size];

  return (
    <HearthBeatLoaderSC {...rest} size={size}>
      <svg width="100%" height="100%" viewBox={viewBox}>
        <defs>
          <linearGradient id="gradient" {...getGradientCords(90.7)}>
            <stop offset="-5.52%" stopColor={theme.color.gradient.loader[0]} />
            <stop offset="31.63%" stopColor={theme.color.gradient.loader[1]} />
            <stop offset="69.34%" stopColor={theme.color.gradient.loader[2]} />
            <stop offset="102.55%" stopColor={theme.color.gradient.loader[3]} />
          </linearGradient>
        </defs>
        <polyline
          className="back"
          points={points}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
          stroke={
            trackColor ??
            (size === 'large'
              ? variant === 'on-dark'
                ? theme.color.surface.secondaryDark
                : theme.color.surface.secondary
              : variant === 'on-dark'
              ? theme.color.surface.loaderDark
              : theme.color.surface.loader)
          }
        ></polyline>
        <polyline
          className="front"
          points={points}
          strokeWidth={strokeWidth}
          stroke={stroke}
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        ></polyline>
      </svg>
    </HearthBeatLoaderSC>
  );
};

export default React.memo(HearthBeatLoader);
